<template>
  <div class="user-manual">
    <el-card shadow="never">
      <div slot="header" class="manual-heading">
        <span>使用说明</span>
        <span class="manual-subtitle">了解您可以查看的数据和选择的人员、网点</span>
      </div>
      <el-tabs :value="currentTopic" @tab-click="changeTopic">
        <el-tab-pane v-for="page in pages" :key="page.key" :label="page.title" :name="page.key" />
      </el-tabs>
      <el-alert title="查看订单、维修单或充电桩列表，需要先开通对应菜单。您有多个角色时，可以同时查看这些角色允许查看的数据。" type="info" :closable="false" show-icon />
      <section v-if="currentTopic === 'roles'">
        <h3>角色说明</h3>
        <p>本手册将角色分为以下三类：</p>
        <dl class="role-guide">
          <div><dt>公司管理</dt><dd>按排除法判断：除“工人师傅、网点师傅、网点客服、网点库管”外的其他有效角色，包括自定义角色。</dd></div>
          <div><dt>师傅</dt><dd>工人师傅、网点师傅。</dd></div>
          <div><dt>网点管理</dt><dd>网点客服、网点库管。</dd></div>
        </dl>
        <p>“客服、统计、管理员”等只是公司管理角色的例子，并非固定名单。账号只要拥有任意一个不在上述四种角色中的有效角色，就具有公司管理身份；没有有效角色时不获得该身份。</p>
      </section>
      <section v-else-if="currentTopic === 'accounts'">
        <h3>账号配置最佳实践</h3>
        <p>先按实际岗位选择角色，再配置该身份使用的数据范围。兼任多个身份时，订单和普通库存的可见范围取并集，增加角色可能扩大可见数据。</p>
        <h4>按岗位配置</h4>
        <ul>
          <li><strong>负责一家公司或多家公司：</strong>选择合适的公司管理角色，配置实际负责的公司；订单再按公司分别配置品牌、省市区。多个授权范围取并集，未配置公司范围时该身份没有公司数据。确需覆盖全部公司时再选择“全部公司”。</li>
          <li><strong>只负责本网点：</strong>选择“网点客服”或“网点库管”，并配置所属网点。只想查看本网点数据时，无需额外添加“客服、统计、管理员”等公司管理角色。</li>
          <li><strong>只负责本人安装业务：</strong>选择“工人师傅”或“网点师傅”，核对本人订单、充电桩的实际归属，并按业务需要维护所属网点。师傅身份查看本人数据，不会仅因配置了网点就看到整个网点的数据。</li>
          <li><strong>兼任网点管理和安装师傅：</strong>按实际职责选择“网点客服／网点库管＋工人师傅／网点师傅”，配置所属网点。可见范围为所属网点数据与本人数据的并集，本人归属在网点外的数据也包含在内。</li>
        </ul>
        <h4>容易混淆的配置</h4>
        <ul>
          <li><strong>“客服”和“网点客服”是两类身份：</strong>“客服＋网点客服”会合并公司范围与网点范围；派单候选按公司管理身份处理，可选择全部符合条件的师傅。</li>
          <li><strong>公司范围配置不会改变角色身份：</strong>仅有网点管理角色时，公司范围不参与该身份的数据过滤。即使配置了公司范围，没有配置所属网点，订单、普通库存和派单候选仍为空。</li>
          <li><strong>新增自定义角色前先确认用途：</strong>自定义角色只要不属于上述四种排除角色，就会归入公司管理。角色名称中带有“网点”或“师傅”字样，不代表自动归入网点管理或师傅身份。</li>
        </ul>
        <h4>推荐配置与验证顺序</h4>
        <ol>
          <li>先确认岗位及是否确实兼任，选择必要的角色。</li>
          <li>开通需要的菜单及操作权限；能看到数据不代表自动拥有派单、编辑或删除权限。</li>
          <li>公司管理身份配置公司范围；网点管理身份配置所属网点；师傅身份核对本人订单和充电桩归属。</li>
          <li>调整角色、菜单或所属网点后重新登录，分别在 PC、移动端核对一笔应当可见和一笔不应可见的数据。</li>
        </ol>
        <p>验证师傅范围时区分“师傅管理”“普通筛选下拉”和“派单候选”。派单候选请参阅“订单与派单”，不能仅凭普通师傅筛选中出现某个人就认定可以派单。</p>
      </section>
      <section v-else-if="currentTopic === 'orders'">
        <h3>订单与维修单：可以查看哪些订单？</h3>
        <ul>
          <li><strong>公司管理：</strong>查看已授权公司范围内的订单。</li>
          <li><strong>师傅：</strong>查看归属自己的订单。</li>
          <li><strong>网点管理：</strong>查看本人所属网点的订单。</li>
        </ul>
        <h4>派单时可以选择哪些师傅？</h4>
        <p>PC 与移动端使用相同规则：拥有公司管理角色时，可以选择全部师傅；仅有网点客服或网点库管角色时，可以选择本人所属网点的师傅。如果同时拥有公司管理和网点管理角色，可以选择全部师傅。没有这两类身份时，派单师傅列表为空。</p>
        <p>候选师傅仍需符合页面上的接单状态及在职筛选条件；可选择师傅不代表可以操作范围外的订单。</p>
        <h4>新增或批量新增时可以选择哪些网点？</h4>
        <p>新增或批量新增订单、维修单时，拥有公司管理角色可以选择全部网点；其他角色只能选择本人所属的网点。</p>
      </section>
      <section v-else-if="currentTopic === 'devices'">
        <h3>充电桩：可以查看哪些充电桩？</h3>
        <ul>
          <li><strong>公司管理：</strong>查看已授权公司范围内的充电桩。</li>
          <li><strong>师傅：</strong>查看本人拥有的充电桩。</li>
          <li><strong>网点管理：</strong>查看本人所属网点的充电桩。</li>
        </ul>
        <h4>列表中的筛选选项如何显示？</h4>
        <p>“所属公司”“所属师傅”“所属网点”和“分配其他”中的选项，来自您可以查看的充电桩。选择筛选条件后，列表会显示其中符合条件的充电桩。</p>
        <h4>新增或批量新增时可以选择哪些网点？</h4>
        <p>拥有公司管理角色时，可以选择全部网点；其他角色只能选择本人所属的网点。</p>
      </section>
      <div class="manual-pagination">
        <el-button size="small" :disabled="pageIndex === 0" @click="navigateTo(pages[pageIndex - 1].key)">上一页</el-button>
        <span>{{ pageIndex + 1 }} / {{ pages.length }}</span>
        <el-button size="small" :disabled="pageIndex === pages.length - 1" @click="navigateTo(pages[pageIndex + 1].key)">下一页</el-button>
      </div>
      <p class="manual-contact">遇到问题请联系管理员张志辉。</p>
    </el-card>
  </div>
</template>
<script>
const pages = Object.freeze([
  { key: 'roles', title: '角色与权限' },
  { key: 'accounts', title: '账号配置' },
  { key: 'orders', title: '订单与派单' },
  { key: 'devices', title: '充电桩管理' }
]);

export default {
  data() {
    return { pages };
  },
  computed: {
    currentTopic() {
      const topic = this.$route.query.topic;
      return pages.some(page => page.key === topic) ? topic : pages[0].key;
    },
    pageIndex() {
      return pages.findIndex(page => page.key === this.currentTopic);
    }
  },
  methods: {
    changeTopic(tab) {
      this.navigateTo(tab.name);
    },
    navigateTo(topic) {
      if (topic === this.currentTopic) return;
      this.$router.push({ path: this.$route.path, query: { ...this.$route.query, topic } });
    }
  }
};
</script>
<style scoped>
.user-manual { padding: 20px; max-width: 1100px; margin: 0 auto; }
.manual-heading { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px 20px; font-size: 16px; font-weight: 600; color: #303133; }
.manual-subtitle { font-size: 13px; font-weight: 400; color: #909399; }
section { padding: 24px 0; border-bottom: 1px solid #ebeef5; }
section:last-child { border-bottom: 0; padding-bottom: 0; }
h3 { margin: 0 0 14px; font-size: 16px; color: #303133; }
h4 { margin: 22px 0 8px; font-size: 14px; color: #303133; }
p, li, dd { font-size: 14px; line-height: 1.9; color: #606266; }
p { margin: 8px 0 0; }
ul, ol { padding-left: 22px; margin: 0; }
li + li { margin-top: 6px; }
strong { color: #303133; font-weight: 500; }
.role-guide { margin: 12px 0 0; }
.role-guide > div { display: flex; gap: 16px; padding: 8px 0; }
.manual-contact { margin-top: 24px; color: #909399; }
.manual-pagination { display: flex; align-items: center; justify-content: center; gap: 20px; margin-top: 24px; color: #909399; font-size: 13px; }
dt { width: 72px; flex-shrink: 0; font-size: 14px; line-height: 1.9; color: #303133; font-weight: 500; }
dd { margin: 0; }
@media (max-width: 768px) { .user-manual { padding: 12px; } .manual-subtitle { line-height: 1.6; } }
</style>
